import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Search, Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';
import { Input } from './ui/input';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * GooglePlacesAutocomplete Component
 * 
 * A reusable autocomplete input that uses Google Places API
 * to suggest verified business listings.
 * 
 * Props:
 * - onPlaceSelect: (placeDetails) => void - Called when user selects a place
 * - placeholder: string - Input placeholder text
 * - initialValue: string - Initial input value
 * - disabled: boolean - Disable the input
 * - className: string - Additional CSS classes
 * - showSelectedDetails: boolean - Show selected place details below input
 * - allowManualEntry: boolean - Allow manual entry if no match found
 * - onManualEntry: (text) => void - Called when user chooses manual entry
 * - types: string - Google Places types filter (default: "establishment")
 */
const GooglePlacesAutocomplete = ({
  onPlaceSelect,
  placeholder = "Search for a venue...",
  initialValue = "",
  disabled = false,
  className = "",
  showSelectedDetails = true,
  allowManualEntry = true,
  onManualEntry,
  types = "establishment"
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [error, setError] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // Generate session token for API cost optimization
  useEffect(() => {
    setSessionToken(crypto.randomUUID());
  }, []);

  // Reset session token after place selection
  const resetSession = useCallback(() => {
    setSessionToken(crypto.randomUUID());
  }, []);

  // Debounced fetch suggestions
  const fetchSuggestions = useCallback(async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        input: query,
        types: types
      });
      
      if (sessionToken) {
        params.append('session_token', sessionToken);
      }

      const response = await axios.get(`${API_URL}/places/autocomplete?${params.toString()}`);
      setSuggestions(response.data.predictions || []);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Autocomplete error:', err);
      setError('Failed to fetch suggestions');
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionToken, types]);

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setSelectedPlace(null);
    setError(null);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce API calls (300ms)
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  // Handle place selection
  const handleSelectPlace = async (prediction) => {
    setIsLoading(true);
    setShowSuggestions(false);
    setInputValue(prediction.name);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (sessionToken) {
        params.append('session_token', sessionToken);
      }

      const response = await axios.get(
        `${API_URL}/places/details/${prediction.place_id}?${params.toString()}`
      );
      
      const placeDetails = response.data;
      setSelectedPlace(placeDetails);
      
      // Call parent callback
      if (onPlaceSelect) {
        onPlaceSelect(placeDetails);
      }

      // Reset session token after successful selection
      resetSession();
    } catch (err) {
      console.error('Place details error:', err);
      setError('Failed to fetch place details');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle manual entry
  const handleManualEntry = () => {
    setShowSuggestions(false);
    if (onManualEntry) {
      onManualEntry(inputValue);
    }
  };

  // Clear selection
  const handleClear = () => {
    setInputValue('');
    setSelectedPlace(null);
    setSuggestions([]);
    setError(null);
    if (onPlaceSelect) {
      onPlaceSelect(null);
    }
    inputRef.current?.focus();
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current?.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`}>
      {/* Input Field */}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400 z-10" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          disabled={disabled || (selectedPlace && !allowManualEntry)}
          className={`pl-10 pr-10 h-12 ${selectedPlace ? 'bg-green-50 border-green-200' : ''}`}
          data-testid="places-autocomplete-input"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400 animate-spin" />
        )}
        {selectedPlace && !isLoading && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
            data-testid="clear-place-btn"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {!selectedPlace && !isLoading && inputValue.length >= 3 && (
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && (suggestions.length > 0 || (inputValue.length >= 3 && allowManualEntry)) && (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-xl shadow-lg z-50 max-h-72 overflow-y-auto"
          data-testid="places-suggestions"
        >
          {suggestions.map((prediction, idx) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => handleSelectPlace(prediction)}
              className="w-full px-4 py-3 text-left hover:bg-stone-50 flex items-start gap-3 border-b border-stone-100 last:border-0 transition-colors"
              data-testid={`suggestion-${idx}`}
            >
              <MapPin className="w-5 h-5 text-stone-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-stone-900">{prediction.main_text}</p>
                <p className="text-sm text-stone-500">{prediction.secondary_text}</p>
              </div>
            </button>
          ))}
          
          {/* Manual entry option */}
          {allowManualEntry && inputValue.length >= 3 && (
            <button
              type="button"
              onClick={handleManualEntry}
              className="w-full px-4 py-3 text-left hover:bg-amber-50 flex items-center gap-3 border-t border-stone-200 text-amber-700"
              data-testid="manual-entry-btn"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <div>
                <p className="font-medium">Can&apos;t find your venue?</p>
                <p className="text-sm text-amber-600">Enter &quot;{inputValue}&quot; manually (unverified)</p>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {error}
        </p>
      )}

      {/* Selected Place Details */}
      {showSelectedDetails && selectedPlace && (
        <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-xl" data-testid="selected-place-details">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-green-900">{selectedPlace.name}</p>
              <p className="text-sm text-green-700 mt-1">{selectedPlace.formatted_address}</p>
              <div className="flex flex-wrap gap-2 mt-2 text-xs text-green-600">
                {selectedPlace.city && <span className="bg-green-100 px-2 py-1 rounded">{selectedPlace.city}</span>}
                {selectedPlace.state && <span className="bg-green-100 px-2 py-1 rounded">{selectedPlace.state}</span>}
                {selectedPlace.pincode && <span className="bg-green-100 px-2 py-1 rounded">{selectedPlace.pincode}</span>}
                {selectedPlace.rating && (
                  <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded">
                    ⭐ {selectedPlace.rating} ({selectedPlace.review_count} reviews)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Minimum characters hint */}
      {inputValue.length > 0 && inputValue.length < 3 && (
        <p className="mt-2 text-xs text-stone-500">Type at least 3 characters to search</p>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;
